import { useState, useEffect } from 'react';
import { AlertCircle, Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';

interface WizardBannerProps {
  configItemId: string;
  configItemName: string;
  totalQuestions: number;
  answeredQuestions: number;
  skippedQuestions: number;
  hasWizard: boolean; // Does this CI have a wizard available?
  onStartWizard: () => void;
  onContinueWizard: () => void;
}

export function WizardBanner({
  configItemId,
  configItemName,
  totalQuestions,
  answeredQuestions,
  skippedQuestions,
  hasWizard,
  onStartWizard,
  onContinueWizard,
}: WizardBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  // Check localStorage for dismissed state on mount
  useEffect(() => {
    const dismissedKey = `wizard_banner_dismissed:${configItemId}`;
    const isDismissed = localStorage.getItem(dismissedKey) === 'true';
    setDismissed(isDismissed);
  }, [configItemId]);

  // Reset dismissed state if user makes progress
  useEffect(() => {
    if (answeredQuestions > 0) {
      const dismissedKey = `wizard_banner_dismissed:${configItemId}`;
      localStorage.removeItem(dismissedKey);
      setDismissed(false);
    }
  }, [answeredQuestions, configItemId]);

  const handleDismiss = () => {
    setDismissed(true);
    const dismissedKey = `wizard_banner_dismissed:${configItemId}`;
    localStorage.setItem(dismissedKey, 'true');
  };

  // Don't show banner if:
  // - No wizard available
  // - User dismissed it
  // - All questions answered (State 4)
  if (!hasWizard || dismissed || answeredQuestions === totalQuestions) {
    return null;
  }

  const unansweredQuestions = totalQuestions - answeredQuestions;
  const completionPercent = totalQuestions > 0 
    ? Math.round((answeredQuestions / totalQuestions) * 100) 
    : 0;

  // State 1: New CI (no progress)
  if (answeredQuestions === 0 && skippedQuestions === 0) {
    return (
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-blue-900 mb-1">
              New to {configItemName}?
            </h4>
            <p className="text-sm text-blue-800 mb-3">
              Our guided wizard makes it easy! Answer questions step-by-step with helpful examples and context.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={onStartWizard}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Start Guided Wizard
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-blue-700 hover:text-blue-900 hover:bg-blue-100"
              >
                I'll answer here
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-blue-400 hover:text-blue-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // State 3: Questions skipped
  if (skippedQuestions > 0) {
    return (
      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-amber-900 mb-1">
              {skippedQuestions === 1 
                ? 'One question skipped' 
                : `${skippedQuestions} questions skipped`
              }
            </h4>
            <p className="text-sm text-amber-800 mb-3">
              These questions are marked below. You can answer them individually or re-enter the wizard.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={onContinueWizard}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Re-enter Wizard
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              >
                I'll answer them here
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-amber-400 hover:text-amber-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // State 2: Wizard in progress (some questions answered, none skipped)
  if (answeredQuestions > 0 && answeredQuestions < totalQuestions) {
    return (
      <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-green-900 mb-1">
              {unansweredQuestions === 1 
                ? 'You have 1 unanswered question' 
                : `You have ${unansweredQuestions} unanswered questions`
              }
            </h4>
            <p className="text-sm text-green-800 mb-2">
              Continue the guided wizard to complete your answers.
            </p>
            <div className="mb-3">
              <div className="flex items-center gap-2 text-sm text-green-700 mb-1">
                <span className="font-medium">Progress: {completionPercent}% complete</span>
                <span className="text-green-400">•</span>
                <span>{answeredQuestions} of {totalQuestions} questions</span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={onContinueWizard}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Continue Wizard
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-green-700 hover:text-green-900 hover:bg-green-100"
              >
                Dismiss
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-green-400 hover:text-green-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // State 4: All questions answered (handled at top - returns null)
  return null;
}
